import { MANILA_TZ } from './constants'

// ── Money ─────────────────────────────────────────────────────────────────────

/** Format integer centavos as ₱ with comma-thousands and 2 decimal places. */
export function formatPrice(centavos: number): string {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(centavos / 100)
}

export function formatPricePerNight(centavos: number): string {
  return `${formatPrice(centavos)} / night`
}

// ── Dates ─────────────────────────────────────────────────────────────────────

/** "May 1, 2026" — formatted in Asia/Manila locale */
export function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-PH', {
    timeZone: MANILA_TZ,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** "May 1 – 3, 2026" for same-month stays; "Apr 29 – May 2, 2026" otherwise */
export function formatDateRange(checkIn: string, checkOut: string): string {
  const inDate  = new Date(checkIn  + 'T00:00:00')
  const outDate = new Date(checkOut + 'T00:00:00')

  const opts: Intl.DateTimeFormatOptions = {
    timeZone: MANILA_TZ,
    month: 'short',
    day: 'numeric',
  }

  const inStr  = inDate.toLocaleDateString('en-PH', opts)
  const outStr = outDate.toLocaleDateString('en-PH', {
    ...opts,
    year: 'numeric',
  })

  return `${inStr} – ${outStr}`
}

/** "2 nights" */
export function formatNights(n: number): string {
  return `${n} ${n === 1 ? 'night' : 'nights'}`
}

/** Today's date in YYYY-MM-DD (Manila calendar day) */
export function todayManila(): string {
  return new Date()
    .toLocaleDateString('en-CA', { timeZone: MANILA_TZ }) // en-CA gives YYYY-MM-DD
}

/** Add n days to a YYYY-MM-DD string, return YYYY-MM-DD */
export function addDays(isoDate: string, days: number): string {
  const d = new Date(isoDate + 'T00:00:00')
  d.setDate(d.getDate() + days)
  return d.toLocaleDateString('en-CA')
}

// ── Misc ──────────────────────────────────────────────────────────────────────

export function formatGuests(adults: number, children: number): string {
  const total = adults + children
  const parts = [`${total} ${total === 1 ? 'guest' : 'guests'}`]
  if (children > 0) parts.push(`${children} ${children === 1 ? 'child' : 'children'}`)
  return parts.join(', ')
}

export function formatRooms(rooms: number): string {
  return `${rooms} ${rooms === 1 ? 'room' : 'rooms'}`
}

/** Booking.com-style score label: 9+ = Superb, 8+ = Excellent, etc. */
export function scoreLabel(score: number): string {
  if (score >= 9)   return 'Superb'
  if (score >= 8)   return 'Excellent'
  if (score >= 7)   return 'Good'
  if (score >= 6)   return 'Pleasant'
  return 'Reviewed'
}
